import {
  EvaluationInsightView,
  EvaluationStatus,
  EvaluationStatusValue,
} from "../../common/Evaluation";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Tooltip,
  Button as MaterialButton,
} from "@mui/material";
import Button from "../editor/toolbar/Button";
import { UserGroup } from "../../common/User";
import { useSelector } from "react-redux";
import { selectAllVesselEmails, VesselEmail } from "../../redux/vessel/vesselEmailSlice";
import { selectCurrentCaptain, selectCurrentUser } from "../../redux/authentication/authSlice";
import { JobRatingLabel } from "../../common/JobOrder";
import { EmailType } from "../../common/EmailDetails";
import EmailModal from "../email/EmailModal";
import { Email } from "@mui/icons-material";
import { formatDateString } from "../../common/FormatDateString";

const EvaluationInsightTable = (props: {
  evalList: EvaluationInsightView[];
  getReminderEmail?: Function;
  emailCallback?: Function;
  handleClick: Function;
  activeEval?: boolean
}) => {
  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const isCaptain = useSelector(selectCurrentCaptain);
  const vesselEmailsMap = useSelector(selectAllVesselEmails);
  const canSeeStatus = (isCaptain || userGroup === UserGroup.FLEET_EXECS || userGroup === UserGroup.VESSEL_EXECS) && props.activeEval;

  const sortInsights = (insights: EvaluationInsightView[]) => {
    return Array.from(insights).sort((a, b) => {
      return (
        Object.keys(EvaluationStatus).indexOf(b.status!) -
        Object.keys(EvaluationStatus).indexOf(a.status!)
      );
    });
  };

  const getCurrentAssessorStage = (insight: EvaluationInsightView) => {
    // Fallback in this a profile is ever null in the database.
    if (!insight.currentAssessorName) {
      return EvaluationStatusValue[insight.status];
    }

    const currentAssessor = `${insight.currentAssessorName}${
      insight.currentAssessorTitle
        ? " (" + insight.currentAssessorTitle.substring(1) + ")"
        : ""
    }`;

    switch (insight.status) {
      case EvaluationStatus.EVALUATION_INITIATED:
        return `${currentAssessor} Starting Evaluation`;
      case EvaluationStatus.ASSESSOR_1_EDIT:
        return `${currentAssessor} Editing`;
      case EvaluationStatus.ASSESSOR_1_SUBMIT:
      case EvaluationStatus.ASSESSOR_2_APPROVE:
        return `${currentAssessor} Reviewing`;
      case EvaluationStatus.ASSESSOR_2_RETURN:
      case EvaluationStatus.FINAL_REVIEWER_RETURN:
        return `${currentAssessor} Rejected`;
      case EvaluationStatus.FINAL_REVIEWER_APPROVE:
        return `Pending ${currentAssessor}'s Signature`;
      case EvaluationStatus.SENIOR_OFFICER_SIGN:
        return `${currentAssessor} Signed`;
      default:
        return "";
    }
  };

  const getCcEmail = (row: any) => {
    const emailsForVessel = vesselEmailsMap[row.vessel.id] || [];
    return emailsForVessel
      .filter( (e: VesselEmail) => e.rating === row.currentAssessorTitle || '')
      .map((e: VesselEmail) => e.email);

  }

  const canSendReminder = (insight: EvaluationInsightView) => {
    return insight.status !== EvaluationStatus.SENIOR_OFFICER_SIGN;
  };

  return (
    <>
      {props.evalList.length > 0 ? (
        <TableContainer>
          <Table className="table" style={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "10%" }}>Vessel</TableCell>
                <TableCell style={{ width: "5%" }}>Rating</TableCell>
                <TableCell style={{ width: "15%" }}>Mariner Name</TableCell>
                {(userGroup === UserGroup.HUMAN_RESOURCES ||
                  userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
                  userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
                  userGroup === UserGroup.CREWING_MANAGERS ||
                  canSeeStatus) && (
                  <TableCell style={{ width: "20%" }}>Current Stage</TableCell>
                )}
                <TableCell style={{ width: "15%" }}>Last Updated</TableCell>
                {(userGroup === UserGroup.HUMAN_RESOURCES ||
                  userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
                  userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
                  userGroup === UserGroup.CREWING_MANAGERS ||
                  (isCaptain && props.activeEval)) && (
                  <TableCell style={{ width: "15%" }}></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortInsights(props.evalList).map((row) => {
                return (
                  <TableRow key={`${row.vessel.name} ${row.profile.id}`}>
                    <TableCell>{row.vessel.name}</TableCell>
                    <TableCell className="left wb-wrap">
                      <div className="rating-inner">
                        <Tooltip
                          title={
                            JobRatingLabel[row.rating! || row.profile.rating!]
                          }
                          placement="right"
                        >
                          <span>
                            {(row.rating || row.profile.rating!).substring(1)}
                          </span>
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        className="blue-link-button"
                        onClick={() => {
                          if (row.dispatchId) {
                            props.handleClick(row.evaluationId, row.dispatchId);
                          } else if (
                            userGroup === UserGroup.HUMAN_RESOURCES ||
                            userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
                            userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
                            userGroup === UserGroup.CREWING_MANAGERS
                          ) {
                            props.handleClick(
                              row.evaluationId,
                              row.vessel,
                              row.profile
                            );
                          } else {
                            props.handleClick(row.evaluationId);
                          }
                        }}
                      >
                        {[
                          row.profile.firstName,
                          row.profile.middleName,
                          row.profile.lastName,
                        ].join(" ")}
                      </Button>
                    </TableCell>
                    {(userGroup === UserGroup.HUMAN_RESOURCES ||
                      userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
                      userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
                      userGroup === UserGroup.CREWING_MANAGERS ||
                      canSeeStatus) && (
                      <TableCell>{getCurrentAssessorStage(row)}</TableCell>
                    )}
                    <TableCell>
                      {formatDateString(row.lastUpdatedDate ?? "")}
                    </TableCell>
                    {(userGroup === UserGroup.HUMAN_RESOURCES ||
                      userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
                      userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
                      userGroup === UserGroup.CREWING_MANAGERS ||
                      (isCaptain && props.activeEval)) && (
                      <TableCell>
                        {canSendReminder(row) && !!row.evaluationId && (
                          <EmailModal
                            key={EmailType.EVALUATION_ALERT}
                            email={props.getReminderEmail!(row)}
                            emailType={EmailType.EVALUATION_ALERT}
                            parentCallback={props.emailCallback!}
                            emailData={{
                              vesselName: row.vessel.name!,
                              rating:
                                JobRatingLabel[
                                  row.rating! || row.profile.rating!
                                ],
                              marinerName: [
                                row.profile.firstName,
                                row.profile.middleName,
                                row.profile.lastName,
                              ].join(" "),
                              evalLink: `${window.location.origin}/evaluations/${row.evaluationId}`,
                              rejectionNote: row.rejectionNote || "",
                            }}
                            emailSubject={`Performance Evaluation Reminder for ${
                              row.vessel.name
                            } ${
                              JobRatingLabel[row.rating || row.profile.rating!]
                            } ${[
                              row.profile.firstName,
                              row.profile.middleName,
                              row.profile.lastName,
                            ].join(" ")}`}
                            cc={getCcEmail(row)}
                          >
                            <div className="flex justify-content-center">
                              <MaterialButton className="blue-link-button">
                                <Email />
                                Remind
                              </MaterialButton>
                            </div>
                          </EmailModal>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <h4 style={{ marginTop: "2%", paddingLeft: "1%" }}>
          No Evaluations to display.
        </h4>
      )}
    </>
  );
};

export default EvaluationInsightTable;
